import { formatTime } from '../utils/dates.ts'
import React, { useEffect, useRef } from 'react'
import { useQuery } from 'react-query'
import toast from 'react-hot-toast'
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline'
import { api } from '../services/api.ts'

interface JobLog {
  timestamp: string
  level: string
  message: string
  city?: string | null
  keyword?: string | null
}

interface JobTerminalProps {
  jobId: string
  isRunning: boolean
}

export default function JobTerminal({ jobId, isRunning }: JobTerminalProps) {
  const terminalRef = useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = React.useState(true)
  const isUserScrolling = useRef(false)

  const { data: logsData } = useQuery(
    ['job-logs', jobId],
    () => api.get(`/api/jobs/${jobId}/logs?limit=1000`),
    {
      enabled: !!jobId,
      refetchInterval: isRunning ? 2000 : false, // Refresh every 2 seconds if job is running
    }
  )

  const logs: JobLog[] = logsData?.data?.logs || []

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && terminalRef.current) {
      isUserScrolling.current = false
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight
    }
  }, [logs, autoScroll])

  const getLogColor = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR':
        return 'text-red-400'
      case 'WARNING':
        return 'text-yellow-400'
      case 'SUCCESS':
        return 'text-green-400'
      case 'INFO':
      default:
        return 'text-gray-300'
    }
  }

  // Pacific time (see utils/dates.ts)
  const formatTimestamp = (timestamp: string) => formatTime(timestamp)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Only disable auto-scroll if this is a user-initiated scroll (not programmatic)
    if (!isUserScrolling.current) {
      isUserScrolling.current = true
      return
    }

    const element = e.currentTarget
    const isAtBottom =
      Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 5

    // Only update if scrolled away from bottom
    if (!isAtBottom && autoScroll) {
      setAutoScroll(false)
    } else if (isAtBottom && !autoScroll) {
      // Re-enable auto-scroll when user scrolls back to bottom
      setAutoScroll(true)
    }
  }

  const [copyState, setCopyState] = React.useState<'idle' | 'copying' | 'copied'>('idle')

  const formatLogLine = (log: JobLog) =>
    `[${formatTimestamp(log.timestamp)}] ${log.level.toUpperCase()} ${log.message}` +
    (log.city ? ` [City: ${log.city}]` : '') +
    (log.keyword ? ` [Keyword: ${log.keyword}]` : '')

  const writeToClipboard = async (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return
    }
    // Fallback for plain-http (e.g. local dev on a LAN IP)
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    if (!ok) throw new Error('copy command failed')
  }

  const handleCopy = async () => {
    setCopyState('copying')
    try {
      // The terminal only holds the last 1000 lines - fetch the full log to copy
      const response = await api.get(`/api/jobs/${jobId}/logs?limit=50000`)
      const allLogs: JobLog[] = response.data?.logs || logs
      await writeToClipboard(allLogs.map(formatLogLine).join('\n'))
      toast.success(`Copied ${allLogs.length} log lines`)
      setCopyState('copied')
      setTimeout(() => setCopyState('idle'), 2000)
    } catch (error) {
      toast.error('Could not copy the log')
      setCopyState('idle')
    }
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked
    setAutoScroll(newValue)

    // If enabling auto-scroll, immediately scroll to bottom
    if (newValue && terminalRef.current) {
      isUserScrolling.current = false
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight
    }
  }

  return (
    <div className='bg-gray-900 rounded-lg overflow-hidden'>
      <div className='bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700'>
        <div className='flex items-center space-x-2'>
          <div className='flex space-x-1.5'>
            <div className='w-3 h-3 rounded-full bg-red-500'></div>
            <div className='w-3 h-3 rounded-full bg-yellow-500'></div>
            <div className='w-3 h-3 rounded-full bg-green-500'></div>
          </div>
          <span className='text-gray-400 text-sm font-mono ml-4'>
            Terminal Output
          </span>
        </div>
        <div className='flex items-center space-x-4'>
          <button
            type='button'
            onClick={handleCopy}
            disabled={copyState === 'copying' || logs.length === 0}
            title='Copy the full log to the clipboard'
            className='flex items-center space-x-1 text-gray-400 hover:text-gray-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {copyState === 'copied' ? (
              <CheckIcon className='h-4 w-4 text-green-400' />
            ) : (
              <ClipboardDocumentIcon className='h-4 w-4' />
            )}
            <span>
              {copyState === 'copying'
                ? 'Copying...'
                : copyState === 'copied'
                ? 'Copied'
                : 'Copy'}
            </span>
          </button>
          <label className='flex items-center space-x-2 text-gray-400 text-sm'>
            <input
              type='checkbox'
              checked={autoScroll}
              onChange={handleCheckboxChange}
              className='rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-900'
            />
            <span>Auto-scroll</span>
          </label>
          {isRunning && (
            <div className='flex items-center space-x-2'>
              <div className='w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
              <span className='text-green-400 text-sm'>Running</span>
            </div>
          )}
        </div>
      </div>

      <div
        ref={terminalRef}
        onScroll={handleScroll}
        className='h-96 overflow-y-auto p-4 font-mono text-sm bg-gray-900 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800'
      >
        {logs.length === 0 ? (
          <div className='text-gray-500 italic'>No logs yet...</div>
        ) : (
          logs.map((log, index) => (
            <div
              key={index}
              className='mb-1 hover:bg-gray-800 px-2 -mx-2 rounded'
            >
              <span className='text-gray-500 mr-2'>
                [{formatTimestamp(log.timestamp)}]
              </span>
              <span className={`${getLogColor(log.level)} mr-2`}>
                {log.level.toUpperCase()}
              </span>
              <span className='text-gray-200'>{log.message}</span>
              {log.city && (
                <span className='text-blue-400 ml-2 text-xs'>
                  [City: {log.city}]
                </span>
              )}
              {log.keyword && (
                <span className='text-purple-400 ml-2 text-xs'>
                  [Keyword: {log.keyword}]
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
